import { HubConnectionBuilder, HubConnectionState, type HubConnection } from '@microsoft/signalr'

import { getToken, subscribe as subscribeAuth, tryRecoverAuth } from '@/lib/auth'
import {
  browserEventChannelKey,
  type BrowserEvent,
  type BrowserEventChannelAddress,
} from '@/lib/browser-events/browser-event'

import { getHubProxyFactory } from './generated/TypedSignalR.Client'
import { issueHubSession } from './hub-session'
import type { IBrowserEventHub } from './generated/TypedSignalR.Client/Platform.Eventing.Contracts.BrowserEvents'

export const BROWSER_EVENT_HUB_URL = '/api/v1/hub'

export type ConnectionState = 'connected' | 'reconnecting' | 'disconnected'

type BrowserEventEnvelope = {
  receiver: string
  method: string
  channel: BrowserEventChannelAddress | null
  arguments: unknown[]
}

type EventListener = {
  event: BrowserEvent<unknown[]>
  invoke: (arguments_: unknown[]) => void
  onResubscribed?: () => void
  resyncOnSubscribe: boolean
}

type ChannelSubscription = {
  count: number
  channel: BrowserEventChannelAddress
  ready?: Promise<void>
}

type HubEntry = {
  connection: HubConnection
  proxy: IBrowserEventHub
  refCount: number
  listeners: Set<EventListener>
  channelSubscriptions: Map<string, ChannelSubscription>
  closeTimer: ReturnType<typeof setTimeout> | null
  backForwardCacheStop: Promise<void> | null
  starting: Promise<void> | null
  retryTimer: ReturnType<typeof setTimeout> | null
  retries: number
  releaseAuthWatch: () => void
  disposed: boolean
}

const RETRY_DELAYS_MS = [2_000, 10_000, 30_000]

const hubs = new Map<string, HubEntry>()
const connectionStates = new Map<string, ConnectionState>()

window.addEventListener('pagehide', pauseForBackForwardCache)
window.addEventListener('pageshow', resumeFromBackForwardCache)

export function getConnectionState(url: string = BROWSER_EVENT_HUB_URL): ConnectionState {
  return connectionStates.get(url) ?? 'disconnected'
}

export function subscribeToBrowserEvent<TArguments extends unknown[]>(
  event: BrowserEvent<TArguments>,
  handler: (...arguments_: NoInfer<TArguments>) => void | Promise<void>,
  onResubscribed?: () => void,
  url: string = BROWSER_EVENT_HUB_URL,
  resyncOnSubscribe = false,
): () => void {
  const entry = acquireEntry(url)
  const listener: EventListener = {
    event,
    invoke: (arguments_) => {
      void handler(...(arguments_ as TArguments))
    },
    onResubscribed,
    resyncOnSubscribe,
  }
  entry.listeners.add(listener)
  if (event.channel) {
    const ready = acquireChannel(entry, event.channel)
    if (ready) void ready.then(() => {
      if (entry.listeners.has(listener) && listener.resyncOnSubscribe) listener.onResubscribed?.()
    })
  } else if (entry.connection.state === HubConnectionState.Connected) {
    queueMicrotask(() => {
      if (entry.listeners.has(listener) && listener.resyncOnSubscribe) listener.onResubscribed?.()
    })
  }

  let subscribed = true
  return () => {
    if (!subscribed) return
    subscribed = false
    entry.listeners.delete(listener)
    if (event.channel) releaseChannel(entry, event.channel)
    releaseEntry(url, entry)
  }
}

function acquireEntry(url: string): HubEntry {
  let entry = hubs.get(url)
  if (entry) {
    if (entry.closeTimer !== null) {
      clearTimeout(entry.closeTimer)
      entry.closeTimer = null
    }
    entry.refCount++
    if (entry.retryTimer === null) void startConnection(url, entry)
    return entry
  }

  entry = createEntry(url)
  entry.refCount = 1
  hubs.set(url, entry)
  void startConnection(url, entry)
  return entry
}

function createEntry(url: string): HubEntry {
  const connection = new HubConnectionBuilder().withUrl(url).withAutomaticReconnect().build()
  const entry: HubEntry = {
    connection,
    proxy: getHubProxyFactory('IBrowserEventHub').createHubProxy(connection),
    refCount: 0,
    listeners: new Set(),
    channelSubscriptions: new Map(),
    closeTimer: null,
    backForwardCacheStop: null,
    starting: null,
    retryTimer: null,
    retries: 0,
    releaseAuthWatch: watchTokenRenewal(),
    disposed: false,
  }

  connection.on('BrowserEvent', (envelope: BrowserEventEnvelope) => dispatch(entry, envelope))
  connection.onreconnecting(() => {
    setState(url, 'reconnecting')
    refreshHubSession()
  })
  connection.onreconnected(() => {
    void restoreSubscriptions(url, entry, true)
  })
  connection.onclose(() => {
    if (hubs.get(url) !== entry) return
    setState(url, 'disconnected')
    scheduleRetry(url, entry)
  })
  return entry
}

function dispatch(entry: HubEntry, envelope: BrowserEventEnvelope): void {
  const envelopeChannel = envelope.channel ? browserEventChannelKey(envelope.channel) : null
  for (const listener of entry.listeners) {
    if (
      listener.event.receiver === envelope.receiver &&
      listener.event.method === envelope.method &&
      (listener.event.channel ? browserEventChannelKey(listener.event.channel) : null) ===
        envelopeChannel
    ) {
      listener.invoke(envelope.arguments)
    }
  }
}

function acquireChannel(entry: HubEntry, channel: BrowserEventChannelAddress): Promise<void> | undefined {
  const key = browserEventChannelKey(channel)
  const subscription = entry.channelSubscriptions.get(key)
  if (subscription) {
    subscription.count++
    return entry.connection.state === HubConnectionState.Connected ? subscription.ready : undefined
  }

  const ready = entry.connection.state === HubConnectionState.Connected
    ? invokeSubscription(entry, 'subscribe', channel) : undefined
  entry.channelSubscriptions.set(key, { count: 1, channel, ready })
  return ready
}

function releaseChannel(entry: HubEntry, channel: BrowserEventChannelAddress): void {
  const key = browserEventChannelKey(channel)
  const subscription = entry.channelSubscriptions.get(key)
  if (!subscription) return
  if (subscription.count > 1) {
    subscription.count--
    return
  }

  entry.channelSubscriptions.delete(key)
  if (entry.connection.state === HubConnectionState.Connected)
    void invokeSubscription(entry, 'unsubscribe', channel)
}

async function restoreSubscriptions(
  url: string,
  entry: HubEntry,
  notifyListeners: boolean,
): Promise<void> {
  const listeners = [...entry.listeners]
  await Promise.all(
    [...entry.channelSubscriptions.values()].map((subscription) => {
      subscription.ready = invokeSubscription(entry, 'subscribe', subscription.channel)
      return subscription.ready
    }),
  )
  if (isHalted(entry)) return
  setState(url, 'connected')
  for (const listener of listeners)
    if (entry.listeners.has(listener) && (notifyListeners || listener.resyncOnSubscribe)) listener.onResubscribed?.()
}

function setState(url: string, state: ConnectionState): void {
  if (connectionStates.get(url) === state) return
  connectionStates.set(url, state)
}

function watchTokenRenewal(): () => void {
  let lastToken = getToken()
  return subscribeAuth(() => {
    const token = getToken()
    if (token === null || token === lastToken) return
    lastToken = token
    refreshHubSession()
  })
}

function refreshHubSession(): void {
  const token = getToken()
  if (token === null) return
  void issueHubSession(token).catch((error: unknown) => {
    console.warn('[browser-events] failed to refresh the hub session', error)
  })
}

async function ensureHubSession(): Promise<void> {
  const token = getToken()
  if (token === null) throw new Error('Cannot open the hub without an access token.')
  await issueHubSession(token)
}

function startConnection(
  url: string,
  entry: HubEntry,
  notifyListeners: boolean = false,
): Promise<void> {
  if (entry.starting) return entry.starting
  if (
    isHalted(entry) ||
    entry.refCount === 0 ||
    entry.connection.state !== HubConnectionState.Disconnected
  )
    return Promise.resolve()
  entry.starting = attemptStart(url, entry, notifyListeners).finally(() => {
    entry.starting = null
  })
  return entry.starting
}

async function attemptStart(url: string, entry: HubEntry, notifyListeners: boolean): Promise<void> {
  try {
    await open(url, entry, notifyListeners)
    return
  } catch (error) {
    if (isHalted(entry)) return
    if (entry.retries > 0 || !(await tryRecoverAuth())) {
      console.warn('[browser-events] hub failed to start', error)
      failStart(url, entry)
      return
    }
  }
  if (isHalted(entry)) return
  try {
    await open(url, entry, notifyListeners)
  } catch (retryError) {
    if (isHalted(entry)) return
    console.warn('[browser-events] hub failed to start after auth recovery', retryError)
    failStart(url, entry)
  }
}

async function open(url: string, entry: HubEntry, notifyListeners: boolean): Promise<void> {
  await ensureHubSession()
  if (isHalted(entry)) return
  await entry.connection.start()
  entry.retries = 0
  if (entry.disposed) return
  await restoreSubscriptions(url, entry, notifyListeners)
}

function isHalted(entry: HubEntry): boolean {
  return entry.disposed || entry.backForwardCacheStop !== null
}

function failStart(url: string, entry: HubEntry): void {
  setState(url, 'disconnected')
  scheduleRetry(url, entry)
}

function scheduleRetry(url: string, entry: HubEntry): void {
  if (isHalted(entry) || entry.retryTimer !== null) return
  entry.retryTimer = setTimeout(() => {
    entry.retryTimer = null
    entry.retries++
    void startConnection(url, entry, true)
  }, RETRY_DELAYS_MS[entry.retries] ?? 30_000)
}

function cancelRetry(entry: HubEntry): void {
  if (entry.retryTimer !== null) clearTimeout(entry.retryTimer)
  entry.retryTimer = null
}

function pauseForBackForwardCache(event: PageTransitionEvent): void {
  if (!event.persisted) return
  for (const [url, entry] of hubs) {
    if (isHalted(entry)) continue
    setState(url, 'disconnected')
    entry.backForwardCacheStop = entry.connection.stop()
    // stop() during a reconnect delay fires onclose synchronously, before the line above assigns.
    cancelRetry(entry)
  }
}

function resumeFromBackForwardCache(event: PageTransitionEvent): void {
  if (!event.persisted) return
  for (const [url, entry] of hubs) {
    const stop = entry.backForwardCacheStop
    if (stop === null) continue
    entry.backForwardCacheStop = null
    void stop.then(() => {
      if (!entry.disposed && hubs.get(url) === entry) void startConnection(url, entry, true)
    })
  }
}

function invokeSubscription(
  entry: HubEntry,
  action: 'subscribe' | 'unsubscribe',
  channel: BrowserEventChannelAddress,
): Promise<void> {
  const call =
    action === 'subscribe'
      ? entry.proxy.subscribeToChannel(channel)
      : entry.proxy.unsubscribeFromChannel(channel)
  return call.catch((error: unknown) => {
    console.warn(`[browser-events] failed to ${action} channel`, channel, error)
  })
}

function releaseEntry(url: string, entry: HubEntry): void {
  entry.refCount--
  if (entry.refCount > 0) return
  entry.closeTimer = setTimeout(() => {
    if (entry.refCount > 0) return
    entry.disposed = true
    cancelRetry(entry)
    entry.releaseAuthWatch()
    hubs.delete(url)
    setState(url, 'disconnected')
    void entry.connection.stop()
  }, 0)
}

export function __resetSignalRForTests(): void {
  for (const entry of hubs.values()) {
    if (entry.closeTimer !== null) clearTimeout(entry.closeTimer)
    cancelRetry(entry)
    entry.releaseAuthWatch()
    entry.disposed = true
  }
  hubs.clear()
  connectionStates.clear()
}
